// The Trainer's queue: who has asked to join, who is approved and still waiting on a
// seat, and what was decided before.
//
// Three sections rather than one filtered table, because they call for three different
// actions: pending rows need a decision, waiting rows need the one manual seat move that
// finishes the flow, and history needs nothing at all. The server buckets them, since
// whether **Accept** can even be offered depends on the org's live free-seat count.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Check, Clock, Inbox, ListFilter, Settings, UserPlus, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import JoinSettingsCard from './JoinSettingsCard';
import { recordJoinRequestsAccessDenied } from './useCanViewJoinRequests';
import ColumnFilterDropdown from '../users/ColumnFilterDropdown';
import { useFilterPanelPosition } from '../users/useFilterPanelPosition';
import { T } from '../gamification/shared';
import { useAuth } from '../../contexts/AuthContext';
import { createLogger } from '../../utils/logger';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import {
    JoinRequest,
    JoinRequestDecision,
    JoinRequestList,
    assignSeat,
    decideJoinRequest,
    fetchJoinRequests,
} from '../../services/joinRequestService';

const logger = createLogger('JoinRequestsPage');

const STATUS_LABELS: Record<string, { label: string; bg: string; fg: string }> = {
    pending: { label: 'Pending', bg: '#dbeafe', fg: '#1e40af' },
    approved_trial: { label: 'Approved with Trial', bg: '#fef3c7', fg: '#92400e' },
    locked_out: { label: 'Locked out', bg: '#fee2e2', fg: '#991b1b' },
    seated: { label: 'Seated', bg: '#dcfce7', fg: '#166534' },
    rejected: { label: 'Rejected', bg: '#f1f5f9', fg: '#475569' },
    lapsed: { label: 'Lapsed', bg: '#f1f5f9', fg: '#475569' },
};

function formatDate(iso?: string | null): string {
    if (!iso) return '—';
    const date = new Date(iso);
    const day = date.toLocaleDateString(undefined, { day: 'numeric' });
    const month = date.toLocaleDateString(undefined, { month: 'long' });
    const year = date.toLocaleDateString(undefined, { year: 'numeric' });
    return `${day} ${month}, ${year}`;
}

/** DD-MM-YYYY — the History table's own date format, distinct from formatDate's
 *  "18 September, 2026" used elsewhere on this page. */
function formatDateShort(iso?: string | null): string {
    if (!iso) return '—';
    const date = new Date(iso);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

/** Shared by every tab's Name/Email column sort — sorts a copy of `rows` by
 *  applicant_name or applicant_email, ascending or descending. */
function sortByNameOrEmail<T extends { applicant_name?: string | null; applicant_email: string }>(
    rows: T[],
    key: 'name' | 'email' | null,
    dir: 'asc' | 'desc'
): T[] {
    if (!key) return rows;
    return [...rows].sort((a, b) => {
        const av = (key === 'name' ? a.applicant_name : a.applicant_email) || '';
        const bv = (key === 'name' ? b.applicant_name : b.applicant_email) || '';
        const cmp = av.localeCompare(bv);
        return dir === 'asc' ? cmp : -cmp;
    });
}

/** Inclusive from/to filter over a row's date field, given as ISO strings from the
 *  <input type="date"> pair a DateRangeColumnFilter edits. */
function withinDateRange<T>(rows: T[], getDate: (row: T) => string | null | undefined, from: string, to: string): T[] {
    let result = rows;
    if (from) {
        const fromMs = new Date(from).getTime();
        result = result.filter(r => {
            const d = getDate(r);
            return d && new Date(d).getTime() >= fromMs;
        });
    }
    if (to) {
        // End-of-day, so a "to" date includes rows dated on that day.
        const toMs = new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1;
        result = result.filter(r => {
            const d = getDate(r);
            return d && new Date(d).getTime() <= toMs;
        });
    }
    return result;
}

/** Table look copied from UsersPage.scss (`.users-table`) so the two management pages
 *  share one visual language instead of this page inventing its own card style. */
const JoinRequestsStyles: React.FC = () => (
    <style>{`
        .jr-table-container {
            border: 1px solid #e4e4e7;
            border-radius: 0.5rem;
            overflow-x: auto;
            overflow-y: hidden;
        }
        .jr-tabbar {
            display: flex;
            gap: 0.25rem;
            background-color: #f4f4f5;
            border: 1px solid #e4e4e7;
            border-radius: 0.5rem;
            padding: 0.25rem;
            margin-bottom: 1.25rem;
            width: fit-content;
        }
        .jr-tab {
            display: inline-flex; align-items: center; gap: 0.4375rem;
            padding: 0.5rem 0.875rem;
            border: none; border-radius: 0.375rem;
            background: transparent; color: #6b7280;
            font-weight: 600; font-size: 0.85rem; cursor: pointer;
            transition: background .15s ease, color .15s ease;
        }
        .jr-tab:focus-visible { outline: 2px solid #4F99BD; outline-offset: 1px; }
        .jr-tab:hover:not(.jr-tab-active) { background-color: rgba(0,0,0,0.04); }
        .jr-tab-active { background-color: #ffffff; color: #005581; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
        .jr-tab-count {
            color: #6b7280; font-weight: 700; background: #e4e4e7;
            border-radius: 9999px; padding: 0.0625rem 0.5rem; font-size: 0.75rem;
        }
        .jr-tab-count-active { color: #005581; background: #eef6fb; }
        .jr-table { width: 100%; min-width: 640px; border-collapse: collapse; background: #fff; }
        .jr-table th, .jr-table td { padding: 1rem; text-align: left; color: #000; }
        .jr-table th { background-color: #f4f4f5; font-weight: 600; font-size: 0.9rem; }
        .jr-table tr { border-bottom: 1px solid #e4e4e7; }
        .jr-table tr:last-child { border-bottom: none; }
        .jr-table tbody tr:hover { background-color: rgba(0,0,0,0.02); }
        .jr-name-cell { font-weight: 500; }
        .jr-actions-cell { text-align: center; white-space: nowrap; }
        .jr-action-buttons { display: inline-flex; align-items: center; gap: 0.5rem; flex-wrap: nowrap; }
        .jr-btn {
            display: inline-flex; align-items: center; gap: 0.375rem;
            border-radius: 0.25rem; cursor: pointer; font-weight: 500; font-size: 0.8rem;
            padding: 0.4rem 0.75rem; transition: all .15s ease;
        }
        .jr-btn:disabled { cursor: not-allowed; opacity: 0.5; }
        .jr-btn-primary { background: linear-gradient(135deg, #4F99BD, #005581); color: #fff; border: none; box-shadow: 0 1px 3px rgba(0,85,129,0.25); }
        .jr-btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .jr-btn-neutral { background: transparent; color: #000; border: 1px solid #e4e4e7; }
        .jr-btn-neutral:hover:not(:disabled) { background-color: #f4f4f5; }
        .jr-btn-danger { background: transparent; color: #000; border: 1px solid #e4e4e7; }
        .jr-btn-danger:hover:not(:disabled) { color: #ef4444; border-color: #ef4444; background: transparent; }
        /* Instant tooltip for disabled action buttons — the native \`title\` attribute
           takes ~1-5s to appear depending on browser/OS, which reads as "nothing
           happens" on click. This shows immediately on hover instead. */
        .jr-tooltip-wrap { position: relative; display: inline-flex; }
        .jr-tooltip-wrap[data-tooltip]:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: calc(100% + 0.4rem);
            left: 50%;
            transform: translateX(-50%);
            background: #1f2937;
            color: #fff;
            font-size: 0.75rem;
            font-weight: 500;
            line-height: 1.4;
            padding: 0.4rem 0.6rem;
            border-radius: 0.375rem;
            white-space: normal;
            width: max-content;
            max-width: 220px;
            text-align: center;
            z-index: 10;
            pointer-events: none;
            box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        .jr-tooltip-wrap[data-tooltip]:hover::before {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: #1f2937;
            z-index: 10;
            pointer-events: none;
        }
        .jr-badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; white-space: nowrap; }
        .jr-badge-pending { background-color: rgba(234,179,8,0.12); color: #92400e; }
        .jr-badge-active { background-color: rgba(16,185,129,0.1); color: #16a34a; }
        .jr-badge-cancelled { background-color: rgba(107,114,128,0.12); color: #6b7280; }
        .jr-badge-locked { background-color: rgba(239,68,68,0.12); color: #b91c1c; }
        .sortable-header {
            display: inline-flex; align-items: center; gap: 0.3rem;
            background: none; border: none; padding: 0; margin: 0;
            font: inherit; font-weight: 600; color: inherit; cursor: pointer;
        }
        .sortable-header:hover { color: #005581; }
        .filterable-header { display: flex; align-items: center; gap: 0.35rem; }
    `}</style>
);

/** className counterpart of UsersPage's `.role-badge` — pending/approved states read as
 *  amber, seated/active as green, rejected/lapsed/locked-out as the two neutral/red tones
 *  Users already uses for "Cancelled". */
const BADGE_CLASS: Record<string, string> = {
    pending: 'jr-badge-pending',
    approved_trial: 'jr-badge-pending',
    locked_out: 'jr-badge-locked',
    seated: 'jr-badge-active',
    rejected: 'jr-badge-cancelled',
    lapsed: 'jr-badge-cancelled',
};

const Badge: React.FC<{ status: string }> = ({ status }) => {
    const label = STATUS_LABELS[status]?.label ?? status;
    return <span className={`jr-badge ${BADGE_CLASS[status] ?? 'jr-badge-cancelled'}`}>{label}</span>;
};

/**
 * Date-of-Action column header filter — same funnel-in-header convention as
 * ColumnFilterDropdown (from/UsersPage), reusing its portal positioning so the panel
 * escapes `.jr-table-container`'s `overflow-y: hidden` the same way. A checkbox list
 * doesn't fit a date range, hence its own small component instead of reusing
 * ColumnFilterDropdown's panel body directly.
 */
const DateRangeColumnFilter: React.FC<{
    from: string;
    to: string;
    onApply: (from: string, to: string) => void;
}> = ({ from, to, onApply }) => {
    const [open, setOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState(from);
    const [draftTo, setDraftTo] = useState(to);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const position = useFilterPanelPosition(open, anchorRef);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggleOpen = () => {
        if (!open) {
            setDraftFrom(from);
            setDraftTo(to);
        }
        setOpen(o => !o);
    };

    const handleClear = () => {
        onApply('', '');
        setDraftFrom('');
        setDraftTo('');
        setOpen(false);
    };

    const handleApply = () => {
        onApply(draftFrom, draftTo);
        setOpen(false);
    };

    const isActive = !!from || !!to;

    return (
        <div className="column-filter" ref={anchorRef}>
            <button
                type="button"
                className={`column-filter__btn${isActive ? ' column-filter__btn--active' : ''}`}
                onClick={toggleOpen}
                aria-label="Filter by Date of Action"
            >
                <ListFilter size={13} />
            </button>

            {open && position && createPortal(
                <div
                    className="column-filter__panel"
                    ref={panelRef}
                    style={{ position: 'fixed', top: position.top, left: position.left, width: 220, padding: '0.6rem 0.9rem' }}
                >
                    <div className="column-filter__title">Filter by Date of Action</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.78rem', color: T.faint }}>
                            Start date
                            <input
                                type="date"
                                value={draftFrom}
                                onChange={e => setDraftFrom(e.target.value)}
                                style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.35rem 0.5rem', border: `1px solid ${T.border}`, borderRadius: '0.3rem', fontSize: '0.83rem' }}
                            />
                        </label>
                        <label style={{ fontSize: '0.78rem', color: T.faint }}>
                            End date
                            <input
                                type="date"
                                value={draftTo}
                                onChange={e => setDraftTo(e.target.value)}
                                style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.35rem 0.5rem', border: `1px solid ${T.border}`, borderRadius: '0.3rem', fontSize: '0.83rem' }}
                            />
                        </label>
                    </div>
                    <div className="column-filter__footer">
                        <button type="button" className="column-filter__clear" onClick={handleClear}>Clear</button>
                        <button type="button" className="column-filter__apply" onClick={handleApply}>Apply</button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

/** The empty-state row for whichever tab has nothing in it. */
const EmptyState: React.FC<{ text: string }> = ({ text }) => (
    <p
        style={{
            margin: 0,
            fontSize: '0.85rem',
            color: T.faint,
            fontStyle: 'italic',
            padding: '14px 16px',
            border: `1px dashed ${T.border}`,
            borderRadius: T.radius,
        }}
    >
        {text}
    </p>
);

type TabKey = 'pending' | 'awaiting' | 'history';

const TAB_META: Record<TabKey, { label: string; icon: React.ReactNode }> = {
    pending: { label: 'Pending', icon: <Clock size={14} /> },
    awaiting: { label: 'Waiting on a seat', icon: <UserPlus size={14} /> },
    history: { label: 'History', icon: <Check size={14} /> },
};

/** Bordered segmented strip, same border color/radius as `.jr-table-container` below it,
 *  so the tab bar reads as part of the same boxed panel rather than a floating row of
 *  underlined links. */
const TabBar: React.FC<{
    active: TabKey;
    counts: Record<TabKey, number>;
    onChange: (tab: TabKey) => void;
}> = ({ active, counts, onChange }) => (
    <div className="jr-tabbar" role="tablist">
        {(Object.keys(TAB_META) as TabKey[]).map(key => {
            const isActive = key === active;
            return (
                <button
                    key={key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onChange(key)}
                    className={`jr-tab${isActive ? ' jr-tab-active' : ''}`}
                >
                    {TAB_META[key].icon}
                    {TAB_META[key].label}
                    <span className={`jr-tab-count${isActive ? ' jr-tab-count-active' : ''}`}>{counts[key]}</span>
                </button>
            );
        })}
    </div>
);

function buttonStyle(kind: 'primary' | 'neutral' | 'danger', disabled: boolean): React.CSSProperties {
    const palette = {
        primary: { bg: T.primaryGrad, fg: '#fff', border: 'transparent' },
        neutral: { bg: T.card, fg: T.muted, border: T.border },
        danger: { bg: T.card, fg: '#b91c1c', border: '#fecaca' },
    }[kind];
    return {
        padding: '8px 16px',
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.fg,
        fontFamily: T.font,
        fontSize: '0.8rem',
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
    };
}

interface ListProps {
    /** The org whose queue this is. Required — see the note on the export below. */
    organizationId: string;
    /** Hides the page heading when embedded in a page that already has one. */
    embedded?: boolean;
    /**
     * Show the [ Settings ] toggle, revealing the join-settings panel inline.
     *
     * This is the ONLY route an organization admin has to these settings: the org detail
     * page that also hosts the panel is super-admin-only, so without it an admin could
     * claim a domain or transfer the Billing Owner by API but never through the product.
     */
    canManageSettings?: boolean;
    /** Gates the trial-seat field inside the settings panel. */
    isSuperAdmin?: boolean;
    /**
     * Called when the server 403s the list because the viewer is a plain Trainer with
     * no approval rights for this org (not the Billing Owner, not an approver, not
     * admin/super admin — see `list_join_requests`). The route-level page uses this to
     * leave entirely rather than show an error, since such a Trainer has nothing to see
     * here at all. Left unset by the org-detail embed, which is only ever rendered for
     * admin/super admin — who never get this 403 regardless of `can_approve`.
     */
    onAccessDenied?: () => void;
}

/**
 * The queue itself, for a named organization.
 *
 * Takes its org id as a prop and reads no auth context, deliberately — the same reason
 * `OrgSurveyCard` does. A host that already knows which org it is showing (the super
 * admin's org detail page) shouldn't have to stand up an AuthProvider to render a list,
 * and `useAuth` throws outright without one.
 */
export const JoinRequestsList: React.FC<ListProps> = ({
    organizationId,
    embedded,
    canManageSettings,
    isSuperAdmin,
    onAccessDenied,
}) => {
    const orgId = organizationId;

    const [activeTab, setActiveTab] = useState<TabKey>('pending');
    const [showSettings, setShowSettings] = useState(false);
    const [data, setData] = useState<JoinRequestList | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    /** Id of the row currently mid-action, so only that row's buttons disable. */
    const [busyId, setBusyId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    // Sort + filter state for each tab's table. Client-side only — the whole list is
    // already in memory from fetchJoinRequests, so there's nothing to round-trip to the
    // server for. Kept as three separate sets of state (rather than one shared set)
    // because switching tabs must not carry a filter over to a table it was never shown
    // on — a Status filter set on History while looking at "seated" rows should not
    // silently hide every row on Pending, which has no Status column at all.
    const [historySortKey, setHistorySortKey] = useState<'name' | 'email' | null>(null);
    const [historySortDir, setHistorySortDir] = useState<'asc' | 'desc'>('asc');
    const [historyActionUserFilter, setHistoryActionUserFilter] = useState<string[]>([]);
    const [historyStatusFilter, setHistoryStatusFilter] = useState<string[]>([]);
    const [historyDateFrom, setHistoryDateFrom] = useState<string>('');
    const [historyDateTo, setHistoryDateTo] = useState<string>('');

    const [pendingSortKey, setPendingSortKey] = useState<'name' | 'email' | null>(null);
    const [pendingSortDir, setPendingSortDir] = useState<'asc' | 'desc'>('asc');
    const [pendingRequestedFrom, setPendingRequestedFrom] = useState<string>('');
    const [pendingRequestedTo, setPendingRequestedTo] = useState<string>('');
    const [pendingExpiresFrom, setPendingExpiresFrom] = useState<string>('');
    const [pendingExpiresTo, setPendingExpiresTo] = useState<string>('');

    const [awaitingSortKey, setAwaitingSortKey] = useState<'name' | 'email' | null>(null);
    const [awaitingSortDir, setAwaitingSortDir] = useState<'asc' | 'desc'>('asc');
    const [awaitingStatusFilter, setAwaitingStatusFilter] = useState<string[]>([]);
    const [awaitingApprovedFrom, setAwaitingApprovedFrom] = useState<string>('');
    const [awaitingApprovedTo, setAwaitingApprovedTo] = useState<string>('');

    const load = useCallback(async () => {
        if (!orgId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setData(await fetchJoinRequests(orgId));
        } catch (err) {
            // A plain Trainer with no approval rights now gets a hard 403 from the
            // server (see list_join_requests) rather than a 200 with can_approve:
            // false — treat it the same way the old can_approve check did, by
            // leaving quietly instead of surfacing a scary "could not load" error.
            if ((err as { status?: number }).status === 403) {
                onAccessDenied?.();
                return;
            }
            logger.error('Failed to load join requests', err);
            setError('Could not load join requests. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        load();
        // onAccessDenied is expected to be a stable callback (e.g. `() => navigate(...)`
        // from the route wrapper) — including it would re-fire this on every render of
        // an inline arrow function passed as a prop.
    }, [load]);

    const act = async (id: string, run: () => Promise<unknown>) => {
        setBusyId(id);
        setActionError(null);
        try {
            await run();
            // Always re-read rather than patching the row in place: a decision changes the
            // org's free-seat count, which decides whether **Accept** is offered on every
            // *other* pending row. Reconciling that on the client would be a second copy
            // of a rule the server already owns.
            await load();
        } catch (err) {
            // Surfaces the API's own message — "no free seat", "already decided", "all
            // trial seats taken" are each actionable and each specific to the attempt.
            const message = err instanceof Error ? err.message : 'That didn’t work. Please try again.';
            logger.warn('Join request action failed', err);
            setActionError(message);
        } finally {
            setBusyId(null);
        }
    };

    const decide = (id: string, decision: JoinRequestDecision) =>
        act(id, () => decideJoinRequest(id, decision));

    const seat = (id: string) => act(id, () => assignSeat(id));

    // Filter options + rows for each tab, derived from whatever's actually in the list
    // (an org's Trainer roster and its rows' statuses change over time, so a hardcoded
    // option list would drift). Hooks, so these run unconditionally ahead of the
    // loading/error early-returns below.
    const historyList = data?.history ?? [];
    const pendingList = data?.pending ?? [];
    const awaitingList = data?.awaiting_seat ?? [];

    const historyActionUserOptions = useMemo(() => {
        const names = new Set<string>();
        historyList.forEach(r => {
            names.add(r.status === 'lapsed' ? 'No response' : r.decided_by_name || '—');
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b)).map(n => ({ value: n, label: n }));
    }, [historyList]);

    const historyStatusOptions = useMemo(() => {
        const statuses = new Set<string>();
        historyList.forEach(r => statuses.add(r.status));
        return Array.from(statuses).map(s => ({ value: s, label: STATUS_LABELS[s]?.label ?? s }));
    }, [historyList]);

    const historyRows = useMemo(() => {
        let rows = historyList;
        if (historyActionUserFilter.length > 0) {
            rows = rows.filter(r => {
                const actionUser = r.status === 'lapsed' ? 'No response' : r.decided_by_name || '—';
                return historyActionUserFilter.includes(actionUser);
            });
        }
        if (historyStatusFilter.length > 0) {
            rows = rows.filter(r => historyStatusFilter.includes(r.status));
        }
        rows = withinDateRange(rows, r => r.decided_at, historyDateFrom, historyDateTo);
        return sortByNameOrEmail(rows, historySortKey, historySortDir);
    }, [historyList, historyActionUserFilter, historyStatusFilter, historyDateFrom, historyDateTo, historySortKey, historySortDir]);

    const pendingRows = useMemo(() => {
        let rows = withinDateRange(pendingList, r => r.created_at, pendingRequestedFrom, pendingRequestedTo);
        rows = withinDateRange(rows, r => r.expires_at, pendingExpiresFrom, pendingExpiresTo);
        return sortByNameOrEmail(rows, pendingSortKey, pendingSortDir);
    }, [pendingList, pendingRequestedFrom, pendingRequestedTo, pendingExpiresFrom, pendingExpiresTo, pendingSortKey, pendingSortDir]);

    const awaitingStatusOptions = useMemo(() => {
        const statuses = new Set<string>();
        awaitingList.forEach(r => statuses.add(r.status));
        return Array.from(statuses).map(s => ({ value: s, label: STATUS_LABELS[s]?.label ?? s }));
    }, [awaitingList]);

    const awaitingRows = useMemo(() => {
        let rows = awaitingList;
        if (awaitingStatusFilter.length > 0) {
            rows = rows.filter(r => awaitingStatusFilter.includes(r.status));
        }
        rows = withinDateRange(rows, r => r.decided_at ?? r.created_at, awaitingApprovedFrom, awaitingApprovedTo);
        return sortByNameOrEmail(rows, awaitingSortKey, awaitingSortDir);
    }, [awaitingList, awaitingStatusFilter, awaitingApprovedFrom, awaitingApprovedTo, awaitingSortKey, awaitingSortDir]);

    const makeSortToggle = (
        sortKey: 'name' | 'email' | null,
        setSortKey: (key: 'name' | 'email') => void,
        setSortDir: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>
    ) => (key: 'name' | 'email') => {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir('asc');
        } else {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        }
    };

    const makeSortIcon = (sortKey: 'name' | 'email' | null, sortDir: 'asc' | 'desc') => (key: 'name' | 'email') => {
        if (sortKey !== key) return <ArrowUpDown size={12} />;
        return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
    };

    const toggleHistorySort = makeSortToggle(historySortKey, setHistorySortKey, setHistorySortDir);
    const historySortIcon = makeSortIcon(historySortKey, historySortDir);

    const togglePendingSort = makeSortToggle(pendingSortKey, setPendingSortKey, setPendingSortDir);
    const pendingSortIcon = makeSortIcon(pendingSortKey, pendingSortDir);

    const toggleAwaitingSort = makeSortToggle(awaitingSortKey, setAwaitingSortKey, setAwaitingSortDir);
    const awaitingSortIcon = makeSortIcon(awaitingSortKey, awaitingSortDir);

    if (loading && !data) return <LoadingIndicator message="" />;

    if (!orgId) {
        // Super admins land here by design: they are platform-wide, so `organization_id`
        // is always empty for them. Their queues are per-organization, on the org page.
        if (isSuperAdmin) {
            return (
                <div
                    style={{
                        margin: 24,
                        padding: '40px 24px',
                        fontFamily: T.font,
                        textAlign: 'center',
                        background: T.card,
                        border: `1px solid ${T.border}`,
                        borderRadius: T.radius,
                        boxShadow: T.shadow,
                    }}
                >
                    <Inbox size={32} color={T.faint} style={{ marginBottom: 12 }} />
                    <p style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 600, color: T.text }}>
                        No organization selected
                    </p>
                    <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: T.muted, lineHeight: 1.6 }}>
                        Join requests belong to an organization. Pick one from Organizations to see and act on its
                        queue.
                    </p>
                    <a
                        href="/organizations"
                        style={{
                            display: 'inline-block',
                            padding: '10px 20px',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#ffffff',
                            background: T.primaryGrad,
                            borderRadius: T.radius,
                            textDecoration: 'none',
                        }}
                    >
                        Go to Organizations
                    </a>
                </div>
            );
        }

        return (
            <div style={{ padding: 24, fontFamily: T.font, color: T.muted, fontSize: '0.9rem', lineHeight: 1.7 }}>
                You don’t belong to an organization, so there are no join requests to show.
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24, fontFamily: T.font }}>
                <p style={{ color: '#991b1b', marginBottom: 12 }}>{error}</p>
                <button onClick={load} style={buttonStyle('primary', false)}>
                    Retry
                </button>
            </div>
        );
    }

    const list = data ?? { pending: [], awaiting_seat: [], history: [], free_seats: null, seat_limit: null, trial_seats_used: 0, trial_seat_limit: 0, can_approve: false };
    // Null means "couldn't determine" (no seat limit set, or the live member count was
    // unavailable) — NOT "none free". Hiding the seat actions is the honest response:
    // offering one the server would then refuse is worse than not offering it.
    const seatsKnown = list.free_seats !== null;
    const hasFreeSeat = seatsKnown && (list.free_seats as number) > 0;
    const canAct = list.can_approve;
    const trialLimit = list.trial_seat_limit;
    // Matches the backend's trial_pool_exhausted exactly: `used >= limit`, with no
    // `limit > 0` guard. A org that set its trial pool to 0 has deliberately opted
    // out of Trial Mode — 0 is a valid "always full" limit, not "no limit to check".
    const trialPoolFull = list.trial_seats_used >= trialLimit;
    const NO_PAID_SEAT_MESSAGE = 'No paid seats left. Purchase an additional seat to accept this applicant.';
    const trialPoolFullMessage =
        trialLimit > 0
            ? `No trial seats left. This org's trial pool is limited to ${trialLimit}.`
            : "This org's trial pool is set to 0 — Trial Mode is turned off.";
    const disabledBtnStyle: React.CSSProperties = { opacity: 0.5, cursor: 'not-allowed' };

    const renderPending = (r: JoinRequest) => (
        <tr key={r.id}>
            <td className="jr-name-cell">{r.applicant_name || '—'}</td>
            <td>{r.applicant_email}</td>
            <td>{formatDate(r.created_at)}</td>
            <td>{formatDate(r.expires_at)}</td>
            {canAct && (
                <td className="jr-actions-cell">
                    <div className="jr-action-buttons">
                        <button
                            className="jr-btn jr-btn-danger"
                            onClick={() => decide(r.id, 'rejected')}
                            disabled={busyId === r.id}
                        >
                            <X size={14} /> Reject
                        </button>
                        <span className="jr-tooltip-wrap" data-tooltip={trialPoolFull ? trialPoolFullMessage : undefined}>
                            <button
                                className="jr-btn jr-btn-neutral"
                                onClick={() => decide(r.id, 'approved_trial')}
                                disabled={busyId === r.id || trialPoolFull}
                                style={trialPoolFull ? disabledBtnStyle : undefined}
                            >
                                <Clock size={14} /> Approve with Trial
                            </button>
                        </span>
                        {/* Always rendered (not hidden) once a seat is unavailable, so the
                            Trainer gets an explicit reason via the tooltip instead of a
                            vanished option — see NO_PAID_SEAT_MESSAGE. Disabled (not just
                            styled) once a seat is unavailable so clicking can't fire the
                            decide() call the server would refuse anyway. A CSS tooltip
                            (not the native title attribute) so the reason shows instantly
                            on hover instead of after the browser's own multi-second delay. */}
                        <span className="jr-tooltip-wrap" data-tooltip={!hasFreeSeat ? NO_PAID_SEAT_MESSAGE : undefined}>
                            <button
                                className="jr-btn jr-btn-primary"
                                onClick={() => decide(r.id, 'seated')}
                                disabled={busyId === r.id || !hasFreeSeat}
                                style={!hasFreeSeat ? disabledBtnStyle : undefined}
                            >
                                <Check size={14} /> Accept
                            </button>
                        </span>
                    </div>
                </td>
            )}
        </tr>
    );

    const renderAwaiting = (r: JoinRequest) => (
        <tr key={r.id}>
            <td className="jr-name-cell">{r.applicant_name || '—'}</td>
            <td>{r.applicant_email}</td>
            <td>
                <Badge status={r.status} />
            </td>
            <td>{formatDate(r.decided_at ?? r.created_at)}</td>
            {canAct && (
                <td className="jr-actions-cell">
                    {hasFreeSeat ? (
                        <button
                            className="jr-btn jr-btn-primary"
                            onClick={() => seat(r.id)}
                            disabled={busyId === r.id}
                        >
                            <UserPlus size={14} /> Move into seat
                        </button>
                    ) : (
                        <span style={{ fontSize: '0.78rem', color: '#6b7280', fontStyle: 'italic' }}>
                            {seatsKnown ? 'No paid seat yet' : 'Seat availability unavailable'}
                        </span>
                    )}
                </td>
            )}
        </tr>
    );

    return (
        <div style={{ padding: embedded ? 0 : '2rem', fontFamily: T.font, width: '100%' }}>
            <JoinRequestsStyles />
            {/* Plain header, matching Users/Organizations: a page title + one-line
                description rather than a colored banner, so switching between the
                admin management pages doesn't feel like switching apps. */}
            {!embedded && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1.5rem',
                    }}
                >
                    <div>
                        <h1
                            id="tour-page-title"
                            style={{ fontSize: '1.75rem', fontWeight: 600, margin: '0 0 0.25rem', color: '#000' }}
                        >
                            Join Requests
                        </h1>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
                            Review who wants in and get them seated
                        </p>
                    </div>
                    {canManageSettings && (
                        <button
                            id="tour-join-settings-toggle"
                            className="jr-btn"
                            onClick={() => setShowSettings(open => !open)}
                            aria-expanded={showSettings}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                background: '#ffffff',
                                color: '#005581',
                                border: '1.5px solid #4F99BD',
                                padding: '0.5rem 1rem',
                                borderRadius: '0.375rem',
                                fontFamily: T.font,
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                alignSelf: 'flex-start',
                            }}
                        >
                            <Settings size={16} /> {showSettings ? 'Hide settings' : 'Settings'}
                        </button>
                    )}
                </div>
            )}

            {/* Collapsed by default: the queue is what a Trainer opens this page for, and
                the settings underneath it change how requests arrive rather than what to
                do with the ones that have. Kept mounted (hidden via CSS) once it's ever
                needed, rather than unmounted on every toggle, so its fetch runs once in
                the background instead of re-running — and re-showing "Loading…" — on
                every click of the Settings button. */}
            {canManageSettings && (
                <div style={{ marginBottom: showSettings ? 22 : 0, display: showSettings ? 'block' : 'none' }}>
                    <JoinSettingsCard organizationId={orgId} isSuperAdmin={isSuperAdmin} />
                </div>
            )}

            {/* Tour anchors are attached only on the standalone page. Embedded in the org
                detail page they would be duplicate ids in one document, and driver.js
                highlights the first match — which would be the wrong org's card. Hidden
                while settings is open: the queue below it isn't relevant to configuring
                how requests arrive, so showing it here is just noise. */}
            <div
                id={embedded ? undefined : 'tour-page-primary-action'}
                style={{
                    display: showSettings ? 'none' : 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 22,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: T.primary,
                    background: '#eef6fb',
                    border: '1px solid #d6ebf5',
                    borderRadius: 999,
                    padding: '6px 14px',
                }}
            >
                <Inbox size={14} />
                {seatsKnown ? (
                    <span>
                        {list.trial_seats_used}/{list.trial_seat_limit} trial seats used
                    </span>
                ) : (
                    <span style={{ color: T.muted, fontWeight: 600 }}>
                        Seat availability couldn’t be determined right now
                    </span>
                )}
            </div>

            {!showSettings && !canAct && (
                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: T.radius,
                        padding: '10px 14px',
                        marginBottom: 20,
                        fontSize: '0.82rem',
                        color: '#92400e',
                        lineHeight: 1.6,
                    }}
                >
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                        You can see this organization’s requests but not act on them. An admin can
                        switch on “Can approve join requests” for your account.
                    </span>
                </div>
            )}

            {!showSettings && actionError && (
                <div
                    style={{
                        background: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: T.radius,
                        padding: '10px 14px',
                        marginBottom: 20,
                        fontSize: '0.82rem',
                        color: '#991b1b',
                        lineHeight: 1.6,
                    }}
                >
                    {actionError}
                </div>
            )}

            <div
                id={embedded ? undefined : 'tour-page-content'}
                style={{ display: showSettings ? 'none' : 'block' }}
            >
                <TabBar
                    active={activeTab}
                    counts={{
                        pending: list.pending.length,
                        awaiting: list.awaiting_seat.length,
                        history: list.history.length,
                    }}
                    onChange={setActiveTab}
                />

                {activeTab === 'pending' && (
                    list.pending.length === 0 ? (
                        <EmptyState text="Nothing waiting on a decision." />
                    ) : (
                        <>
                            {(pendingRequestedFrom || pendingRequestedTo || pendingExpiresFrom || pendingExpiresTo) && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPendingRequestedFrom('');
                                            setPendingRequestedTo('');
                                            setPendingExpiresFrom('');
                                            setPendingExpiresTo('');
                                        }}
                                        style={{ padding: '0.35rem 0.65rem', border: `1px solid ${T.border}`, borderRadius: '0.375rem', fontSize: '0.8rem', background: 'transparent', cursor: 'pointer', color: T.muted }}
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            )}

                            {pendingRows.length === 0 ? (
                                <EmptyState text="No requests match these filters." />
                            ) : (
                                <div className="jr-table-container">
                                    <table className="jr-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <button type="button" className="sortable-header" onClick={() => togglePendingSort('name')}>
                                                        Name {pendingSortIcon('name')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <button type="button" className="sortable-header" onClick={() => togglePendingSort('email')}>
                                                        Email {pendingSortIcon('email')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Requested</span>
                                                        <DateRangeColumnFilter
                                                            from={pendingRequestedFrom}
                                                            to={pendingRequestedTo}
                                                            onApply={(from, to) => {
                                                                setPendingRequestedFrom(from);
                                                                setPendingRequestedTo(to);
                                                            }}
                                                        />
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Expires</span>
                                                        <DateRangeColumnFilter
                                                            from={pendingExpiresFrom}
                                                            to={pendingExpiresTo}
                                                            onApply={(from, to) => {
                                                                setPendingExpiresFrom(from);
                                                                setPendingExpiresTo(to);
                                                            }}
                                                        />
                                                    </div>
                                                </th>
                                                {canAct && <th className="jr-actions-cell">Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>{pendingRows.map(renderPending)}</tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )
                )}

                {activeTab === 'awaiting' && (
                    list.awaiting_seat.length === 0 ? (
                        <EmptyState text="Nobody is waiting for a seat." />
                    ) : (
                        <>
                            {(awaitingStatusFilter.length > 0 || awaitingApprovedFrom || awaitingApprovedTo) && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAwaitingStatusFilter([]);
                                            setAwaitingApprovedFrom('');
                                            setAwaitingApprovedTo('');
                                        }}
                                        style={{ padding: '0.35rem 0.65rem', border: `1px solid ${T.border}`, borderRadius: '0.375rem', fontSize: '0.8rem', background: 'transparent', cursor: 'pointer', color: T.muted }}
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            )}

                            {awaitingRows.length === 0 ? (
                                <EmptyState text="No requests match these filters." />
                            ) : (
                                <div className="jr-table-container">
                                    <table className="jr-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <button type="button" className="sortable-header" onClick={() => toggleAwaitingSort('name')}>
                                                        Name {awaitingSortIcon('name')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <button type="button" className="sortable-header" onClick={() => toggleAwaitingSort('email')}>
                                                        Email {awaitingSortIcon('email')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Status</span>
                                                        <ColumnFilterDropdown
                                                            label="Status"
                                                            options={awaitingStatusOptions}
                                                            selected={awaitingStatusFilter}
                                                            onApply={setAwaitingStatusFilter}
                                                        />
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Approved</span>
                                                        <DateRangeColumnFilter
                                                            from={awaitingApprovedFrom}
                                                            to={awaitingApprovedTo}
                                                            onApply={(from, to) => {
                                                                setAwaitingApprovedFrom(from);
                                                                setAwaitingApprovedTo(to);
                                                            }}
                                                        />
                                                    </div>
                                                </th>
                                                {canAct && <th className="jr-actions-cell">Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>{awaitingRows.map(renderAwaiting)}</tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )
                )}

                {activeTab === 'history' && (
                    list.history.length === 0 ? (
                        <EmptyState text="No past decisions yet." />
                    ) : (
                        <>
                            {(historyActionUserFilter.length > 0 || historyStatusFilter.length > 0 || historyDateFrom || historyDateTo) && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setHistoryActionUserFilter([]);
                                            setHistoryStatusFilter([]);
                                            setHistoryDateFrom('');
                                            setHistoryDateTo('');
                                        }}
                                        style={{ padding: '0.35rem 0.65rem', border: `1px solid ${T.border}`, borderRadius: '0.375rem', fontSize: '0.8rem', background: 'transparent', cursor: 'pointer', color: T.muted }}
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            )}

                            {historyRows.length === 0 ? (
                                <EmptyState text="No decisions match these filters." />
                            ) : (
                                <div className="jr-table-container">
                                    <table className="jr-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    <button
                                                        type="button"
                                                        className="sortable-header"
                                                        onClick={() => toggleHistorySort('name')}
                                                    >
                                                        User Name {historySortIcon('name')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <button
                                                        type="button"
                                                        className="sortable-header"
                                                        onClick={() => toggleHistorySort('email')}
                                                    >
                                                        Email {historySortIcon('email')}
                                                    </button>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Date of Action</span>
                                                        <DateRangeColumnFilter
                                                            from={historyDateFrom}
                                                            to={historyDateTo}
                                                            onApply={(from, to) => {
                                                                setHistoryDateFrom(from);
                                                                setHistoryDateTo(to);
                                                            }}
                                                        />
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Action User</span>
                                                        <ColumnFilterDropdown
                                                            label="Action User"
                                                            options={historyActionUserOptions}
                                                            selected={historyActionUserFilter}
                                                            onApply={setHistoryActionUserFilter}
                                                        />
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="filterable-header">
                                                        <span>Status</span>
                                                        <ColumnFilterDropdown
                                                            label="Status"
                                                            options={historyStatusOptions}
                                                            selected={historyStatusFilter}
                                                            onApply={setHistoryStatusFilter}
                                                        />
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyRows.map(r => (
                                                <tr key={r.id}>
                                                    <td className="jr-name-cell">{r.applicant_name || '—'}</td>
                                                    <td>{r.applicant_email}</td>
                                                    <td>{formatDateShort(r.decided_at)}</td>
                                                    <td>{r.status === 'lapsed' ? 'No response' : r.decided_by_name || '—'}</td>
                                                    <td>
                                                        <Badge status={r.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )
                )}
            </div>
        </div>
    );
};

/**
 * The route-level page: the same queue, for whichever org the signed-in Trainer belongs
 * to. This is the only place auth is read, so the list above stays usable anywhere.
 */
const JoinRequestsPage: React.FC = () => {
    const { currentUser, userRole } = useAuth();
    const navigate = useNavigate();
    const isSuperAdmin = userRole === 'super_admin';
    const canManageSettings = isSuperAdmin || userRole === 'admin' || userRole === 'trainer';
    return (
        <JoinRequestsList
            organizationId={currentUser?.organization_id ?? ''}
            canManageSettings={canManageSettings}
            isSuperAdmin={isSuperAdmin}
            // A plain Trainer with no approval rights has nothing to decide here —
            // send them back to Home rather than show an empty, action-less queue, and
            // drop the sidebar link that led here (rights revoked mid-session).
            onAccessDenied={() => {
                recordJoinRequestsAccessDenied(currentUser?.id, currentUser?.organization_id);
                navigate('/', { replace: true });
            }}
        />
    );
};

export default JoinRequestsPage;
